<script setup lang="ts">
import EventusAssociationHeader from "../../../assets/image/eventUs/eventUs-association-header.jpg";
import IconEventusAssociation from "../../../assets/image/eventUs/icons/icon-EventUs-association.svg";
import EventusAssociationTitle from "../../../assets/image/eventUs/eventUs-association-title.svg";
import IconMedals from "../../../assets/image/eventUs/icons/all-medals.svg";
import EventUsNavigation from "../../../components/website/EventUsNavigation.vue";
import ImageHeader from "../../../components/website/ImageHeader.vue";
import Who from "../../../components/website/LayoutForWho.vue";
import { useTranslate } from "addeus-common-library/stores/translate";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { useOwner } from "../../../stores/website/owner";
import PackCard from "../../../components/website/PackCard.vue";
import VFlex from "addeus-common-library/components/base/flex/VFlex.vue";
import TranslateNamespace from "addeus-common-library/components/base/translation/TranslateNamespace.vue";
import OptionCard from "../../../components/website/OptionCard.vue";
import { useSeoMeta } from "../../../composables/useSeoMeta";

const breakpoints = useBreakpoints(breakpointsTailwind);
const isBetweenLgMd = breakpoints.between("md", "lg");
const smallerThanSm = breakpoints.smallerOrEqual("sm");
const { translate } = useTranslate();

const { setTranslateNamespace } = useTranslate();
setTranslateNamespace(".eventUs.association");

useSeoMeta({
    title: "Karting pour associations | Formule Racing 1901",
    description:
        "Formule karting électrique pour associations loi 1901 : Racing 1901 avec Grand Prix, podium, médailles et option initiation mécanique. Piste privatisée, activité groupe conviviale.",
    path: "/event-us/association",
    breadcrumbs: [
        { name: "Accueil", path: "/" },
        { name: "Événements chez nous", path: "/event-us" },
        { name: "Associations", path: "/event-us/association" },
    ],
    schemaOrg: [
        {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Racing 1901 - Karting associations",
            description:
                "Formule karting pour associations avec Grand Prix, médailles et initiation mécanique.",
            url: "https://speed2max.com/event-us/association",
            provider: {
                "@type": "Organization",
                name: "Speed2Max",
                url: "https://speed2max.com",
            },
            serviceType: "Karting pour associations",
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
                {
                    "@type": "Question",
                    name: "Que comprend la formule Racing 1901 ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "La formule Racing 1901 comprend 3 runs (qualification, demi-finale, finale), un podium avec médailles et une consommation. Une option initiation mécanique est également disponible.",
                    },
                },
                {
                    "@type": "Question",
                    name: "La formule Racing 1901 est-elle réservée aux associations loi 1901 ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "La formule est conçue pour les associations mais reste ouverte à tous les groupes souhaitant une formule conviviale avec Grand Prix et podium.",
                    },
                },
            ],
        },
    ],
});

const ownerManager = useOwner();
</script>

<template>
    <VFlex flex-direction="column" class="has-background-dark">
        <EventUsNavigation current="association"></EventUsNavigation>
        <ImageHeader secondary>
            <template #image>
                <img
                    :src="EventusAssociationHeader"
                    alt="Karting pour associations Racing 1901 Speed2Max" />
            </template>
            <template #title>
                <span> <Translate path=".title"></Translate></span>
            </template>
            <template #subtitle>
                <span class="has-text-white">
                    <Translate path=".subtitle"></Translate>&nbsp;
                    <span class="has-text-primary">
                        <Translate path=".subtitle2"></Translate>&nbsp;
                    </span>
                    <Translate path=".subtitle3"></Translate>
                </span>
            </template>
        </ImageHeader>
        <Who>
            <template #title>
                <Translate path=".title"></Translate>
            </template>
            <template #content>
                <Translate path=".content"></Translate>
            </template>
            <template #icon>
                <img :src="IconEventusAssociation" alt="Icône karting association" />
            </template>
            <template #titleP1>
                <Translate path=".titleP1"></Translate>
            </template>
            <template #titleP2>
                <Translate path=".titleP2"></Translate>
            </template>
        </Who>
        <div class="pack-grid">
            <PackCard
                color="silver"
                :head-image="EventusAssociationTitle"
                :price="ownerManager.owner?.website?.priceRacing1901"
                class="pack-item">
                <template #content>
                    <TranslateNamespace path=".pack">
                        <VFlex
                            flex-direction="column"
                            class="is-flex is-align-self-stretch py-3 is-size-content"
                            align-items="start">
                            <p class="has-text-white is-uppercase my-2 is-weight-600">
                                <span class="has-text-silver">•&nbsp; 3</span>
                                <Translate path=".feature1"></Translate>
                            </p>
                            <p
                                class="has-text-white is-uppercase my-2 mx-4 is-weight-600">
                                <span class="has-text-silver">•&nbsp; RUN 1 </span>
                                <Translate path=".feature11"></Translate>
                            </p>
                            <p
                                class="has-text-white is-uppercase my-2 mx-4 is-weight-600">
                                <span class="has-text-silver">•&nbsp; RUN 2 </span>
                                <Translate path=".feature12"></Translate>
                            </p>
                            <p
                                class="has-text-white is-uppercase my-2 mx-4 is-weight-600">
                                <span class="has-text-silver">•&nbsp; RUN 3 </span>
                                <Translate path=".feature13"></Translate>
                            </p>
                            <VFlex
                                :flex-direction="
                                    isBetweenLgMd
                                        ? 'column'
                                        : smallerThanSm
                                        ? 'column'
                                        : 'row'
                                ">
                                <p
                                    class="has-text-white is-uppercase my-2 mr-3 is-weight-600">
                                    <span class="has-text-silver">•&nbsp;</span>
                                    <Translate path=".feature2"></Translate>
                                </p>
                                <VFlex flex-direction="row">
                                    <img
                                        :src="IconMedals"
                                        style="height: 2rem"
                                        class="mx-2 my-auto"
                                        alt="Médailles podium Racing 1901" />
                                </VFlex>
                            </VFlex>
                        </VFlex>
                    </TranslateNamespace>
                </template>
            </PackCard>
            <OptionCard
                :name="translate('.pack.option.name', {}).value"
                :content="translate('.pack.option.content', {}).value"
                :title="translate('.pack.option.title', {}).value"
                :price="ownerManager.owner?.website?.priceRacing1901Option"
                class="option-item my-auto"
                :class="{ 'mr-6': !smallerThanSm, 'mx-6': smallerThanSm }"></OptionCard>
        </div>
        <VFlex class="px-6 py-5 mb-5" flex-direction="column">
            <VFlex v-if="ownerManager.ownerID === null" :flex-direction="'column'">
                <p class="is-size-6 mb-4">
                    <Translate path=".infoChooseOwner"></Translate>
                </p>
                <ButtonPrimary
                    color="primary"
                    class="is-align-self-flex-start mt-none is-mohol is-size-6"
                    style="font-family: mohol !important"
                    @click="$router.push('/owners')">
                    <!-- <VIcon icon="/images/kart/book-button-1.png"></VIcon> -->
                    <span class="is-uppercase">
                        <Translate path=".selectOwner"></Translate>
                    </span>
                </ButtonPrimary>
            </VFlex>
            <Banner class="m-5 has-gradient-background-dark-ltr w-100 is-weight-600">
                <Translate path=".banner"></Translate><br />
                <a
                    v-if="ownerManager.owner"
                    class="is-weight-900"
                    :href="`tel:${ownerManager.owner?.phoneNumber}`"
                    >{{ ownerManager.owner?.phoneNumber }}</a
                >
            </Banner>
        </VFlex>

        <Footer></Footer>
    </VFlex>
</template>
<style scoped lang="scss">
@use "sass:color";

.pack-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(2, 1fr);

    > .pack-item {
        grid-column-start: 1;
        grid-column-end: 3;
        grid-row-start: 1;
        grid-row-end: 3;
    }

    > .option-item {
        grid-column-start: 3;
        grid-column-end: 4;
        grid-row-start: 1;
        grid-row-end: 2;
    }
}

@media screen and (max-width: 1360px) {
    .pack-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(2, 1fr) 0.6fr;

        > .pack-item {
            grid-column-start: 1 !important;
            grid-column-end: 3 !important;
            grid-row-start: 1 !important;
            grid-row-end: 3 !important;
        }

        > .option-item {
            grid-column-start: 1 !important;
            grid-column-end: 3 !important;
            grid-row-start: 3 !important;
            grid-row-end: 4 !important;
        }
    }
}

.title-pack-association {
    max-height: 100px;
}

.w-100 {
    width: 100%;
}

.pack-block {
    position: relative;
    border-radius: 5px;

    .is-size-content {
        font-size: 1.5rem;
    }

    .top-right-img {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 1;
        max-height: 24%;
    }

    @media (max-width: 865px) {
        .top-right-img {
            max-height: 100px;
        }

        .title-pack-association {
            max-height: 70px;
        }
    }

    @media (max-width: 700px) {
        .is-size-content {
            font-size: 1.2rem;
        }
    }
}

.option {
    background: radial-gradient(circle, $dark 0%, $blue 100%);
    background-size: 200% 200%;
    animation: gradient 20s ease infinite;

    &:nth-child(1) {
        animation-delay: 0.25s;
    }

    &:nth-child(2) {
        animation-delay: 0.5s;
    }
}

@keyframes gradient {
    0% {
        background-position: 0% 100%;
    }

    50% {
        background-position: 100% 0%;
    }

    100% {
        background-position: 0% 100%;
    }
}
</style>
